import { X } from 'lucide-react'
import { Suspense } from 'react'

import { Button } from 'src/shared/components/ui/buttons/button'
import DashboardModal from 'src/shared/components/ui/dashboard/dashboard-modal'
import { Skeleton } from 'src/shared/components/ui/miscellaneous/skeleton'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useToast } from 'src/shared/hooks/use-toast'

import { useEnterpriseInvitationById } from '../api/use-enterprise-invitation'
import { useMakeEnterpriseInvitationDecision } from '../api/use-make-enterprise-invitation-decision'
import { EnterpriseInvitationStatus } from '../schemas/enterprise'

type ManageEnterpriseInviteModalProps = {
  open: boolean
  onClose: () => void
  invitationId: number | string
  onDecision?: (status: EnterpriseInvitationStatus) => void
}

type ManageEnterpriseInviteModalContentProps = {
  onClose: () => void
  invitationId: number | string
  onDecision?: (status: EnterpriseInvitationStatus) => void
}

const InviteVisualFallback = () => {
  return (
    <div className="mb-4 mt-1 flex w-full justify-center">
      <div className="relative" style={{ width: 175, height: 145 }}>
        <Skeleton className="absolute left-0 top-0 size-[100px] rounded-[20px]" />
        <Skeleton className="absolute left-[75px] top-[45px] size-[100px] rounded-[20px]" />
      </div>
    </div>
  )
}

const ManageEnterpriseInviteModalFallback = ({ onClose }: { onClose: () => void }) => {
  return (
    <div className="relative w-[450px] rounded-[30px] bg-white p-10">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute right-5 top-5 flex size-12 items-center justify-center rounded-full bg-accent text-primary"
      >
        <X size={18} />
      </button>

      <InviteVisualFallback />

      <div className="flex flex-col items-center gap-2.5">
        <Skeleton className="h-10 w-[220px] rounded-md" />
        <Skeleton className="h-5 w-[260px] rounded-md" />
      </div>

      <div className="mt-8 flex items-center gap-3">
        <Button variant="outline" className="h-[50px] flex-1 rounded-full border-primary text-[15px]">
          Reject
        </Button>
        <Button variant="default" className="h-[50px] flex-[1.5] rounded-full text-[15px]">
          Accept invitation
        </Button>
      </div>
    </div>
  )
}

const ManageEnterpriseInviteModalContent = ({
  onClose,
  invitationId,
  onDecision,
}: ManageEnterpriseInviteModalContentProps) => {
  const { toast } = useToast()
  const { data: currentUser } = useMyUserData()
  const { data: invitation } = useEnterpriseInvitationById(invitationId)
  const { mutateAsync: makeEnterpriseInvitationDecision, isPending } = useMakeEnterpriseInvitationDecision()

  const userInitial =
    [currentUser?.firstName?.charAt(0), currentUser?.lastName?.charAt(0)].filter(Boolean).join('') || 'U'
  const enterpriseInitial = invitation?.enterpriseName?.charAt(0)?.toUpperCase() || 'C'

  const onMakeDecision = async (status: EnterpriseInvitationStatus) => {
    if (!invitation?.id) {
      toast({
        variant: 'destructive',
        title: 'Invitation unavailable',
        description: 'We could not find this invitation. Please refresh and try again.',
      })
      return
    }

    if (currentUser?.enterpriseId) {
      toast({
        variant: 'destructive',
        title: 'You are already a member of an enterprise',
      })
      return
    }

    try {
      const response = await makeEnterpriseInvitationDecision({
        invitationId: invitation.id,
        status,
      })

      if (response) {
        toast({
          variant: 'default',
          title: `Invitation ${status === EnterpriseInvitationStatus.ACCEPTED ? 'accepted' : 'rejected'}`,
        })
        onClose()
        onDecision?.(status)
      } else {
        toast({
          variant: 'destructive',
          title: `Error ${status === EnterpriseInvitationStatus.ACCEPTED ? 'accepting' : 'rejecting'} invitation`,
          description: 'Unknown error',
        })
      }
    } catch (error) {
      console.error('Error making enterprise invitation decision', error)
      toast({
        variant: 'destructive',
        title: `Error ${status === EnterpriseInvitationStatus.ACCEPTED ? 'accepting' : 'rejecting'} invitation`,
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  return (
    <div className="relative w-[450px] rounded-[30px] bg-white p-10">
      <button
        type="button"
        aria-label="Close"
        disabled={isPending}
        onClick={onClose}
        className="absolute right-5 top-5 flex size-12 items-center justify-center rounded-full bg-accent text-primary disabled:opacity-60"
      >
        <X size={24} />
      </button>

      <div className="mb-6 mt-4 flex w-full justify-center">
        <div className="relative" style={{ width: 175, height: 145 }}>
          <div className="absolute left-0 top-0 flex size-[100px] items-center justify-center overflow-hidden rounded-[20px] bg-border text-xl font-semibold text-primary">
            {currentUser?.profilePhoto ? (
              <img src={currentUser.profilePhoto} alt={currentUser.fullName || ''} className="size-full object-cover" />
            ) : (
              userInitial
            )}
          </div>
          <div className="absolute left-[75px] top-[45px] flex size-[100px] items-center justify-center overflow-hidden rounded-[20px] bg-primary text-[54px] font-semibold leading-none text-white">
            {invitation?.enterpriseLogoPicture ? (
              <img
                src={invitation.enterpriseLogoPicture}
                alt={invitation.enterpriseName || ''}
                className="size-full object-cover"
              />
            ) : (
              enterpriseInitial
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-2.5">
        <h1 className="text-center text-[26px] font-semibold leading-[1.2] tracking-[-0.26px] text-primary">
          You&apos;ve been invited!
        </h1>
        <p className="text-center text-[15px] leading-normal text-muted">
          {invitation?.inviterName
            ? `${invitation.inviterName} invited you to join their team on Conrad.`
            : 'You have received an enterprise invitation on Conrad.'}
        </p>
      </div>

      {!currentUser?.enterpriseId && (
        <div className="mt-8 flex items-center gap-3">
          <Button
            variant="outline"
            className="h-[55px] flex-1 rounded-full border-primary text-[15px]"
            disabled={isPending}
            isLoading={isPending}
            onClick={() => onMakeDecision(EnterpriseInvitationStatus.REJECTED)}
          >
            Reject
          </Button>
          <Button
            variant="default"
            className="h-[55px] flex-[2] rounded-full bg-primary text-[15px] text-white"
            disabled={isPending}
            isLoading={isPending}
            onClick={() => onMakeDecision(EnterpriseInvitationStatus.ACCEPTED)}
          >
            Accept invitation
          </Button>
        </div>
      )}
    </div>
  )
}

const ManageEnterpriseInviteModal = ({ open, onClose, invitationId, onDecision }: ManageEnterpriseInviteModalProps) => {
  return (
    <DashboardModal open={open} onClose={onClose}>
      <Suspense fallback={<ManageEnterpriseInviteModalFallback onClose={onClose} />}>
        <ManageEnterpriseInviteModalContent invitationId={invitationId} onClose={onClose} onDecision={onDecision} />
      </Suspense>
    </DashboardModal>
  )
}

export default ManageEnterpriseInviteModal
